import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography, withStyles } from '@material-ui/core';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import styles from './pdf-viewer.style';

function readScrollHostWidth(el) {
  if (!el) return 0;
  const fromClient = el.clientWidth;
  if (fromClient > 0) {
    return Math.max(0, Math.floor(fromClient));
  }
  const rect = el.getBoundingClientRect();
  return Math.max(0, Math.floor(rect.width));
}

const PDF_VIEWER_STRINGS = {
  loading: 'Loading document',
  error: "We couldn't load this PDF. Try again or open the file in a new tab.",
  ariaRegion: 'PDF document',
};

/**
 * Admin PDF viewer: native-style continuous scroll (all pages), width from container.
 * Plain strings (no react-intl).
 */
const PdfViewer = ({
  classes,
  fileUrl,
  fileName,
  renderMode,
  maxWidth,
  className,
  style,
}) => {
  const scrollRef = useRef(null);
  const lastStableWidthRef = useRef(0);
  const [numPages, setNumPages] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(fileUrl));
  const [columnWidth, setColumnWidth] = useState(0);

  useEffect(() => {
    setNumPages(null);
    setLoadError(false);
    setIsLoading(Boolean(fileUrl));
  }, [fileUrl]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    lastStableWidthRef.current = 0;

    const applyWidth = (w) => {
      const measured =
        w != null && Number.isFinite(w) && w > 0
          ? Math.max(0, Math.floor(w))
          : readScrollHostWidth(el);

      if (measured > 0) {
        lastStableWidthRef.current = measured;
        setColumnWidth(measured);
        return;
      }

      if (lastStableWidthRef.current > 0) {
        return;
      }

      setColumnWidth(0);
    };

    applyWidth(readScrollHostWidth(el));

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect?.width;
      if (cr != null && Number.isFinite(cr) && cr > 0) {
        applyWidth(cr);
        return;
      }
      applyWidth(readScrollHostWidth(el));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fileUrl]);

  const onDocumentLoadSuccess = useCallback((pdf) => {
    setNumPages(pdf.numPages);
    setLoadError(false);
    setIsLoading(false);
  }, []);

  const onDocumentLoadError = useCallback(() => {
    setLoadError(true);
    setIsLoading(false);
  }, []);

  const horizontalInset =
    columnWidth > 0 ? Math.min(16, Math.max(4, Math.floor(columnWidth * 0.04))) : 0;
  const pageWidth =
    columnWidth > 0
      ? Math.max(1, columnWidth - horizontalInset * 2)
      : null;

  const canRenderPages =
    !loadError && numPages != null && numPages > 0 && pageWidth != null && pageWidth >= 1;

  const waitingForLayout =
    !loadError &&
    numPages != null &&
    numPages > 0 &&
    (pageWidth == null || pageWidth < 1);

  const showBlockingOverlay = isLoading || waitingForLayout;

  if (!fileUrl) {
    return null;
  }

  const rootStyle = {
    ...(maxWidth != null ? { maxWidth } : {}),
    ...style,
  };

  const regionLabel = fileName || PDF_VIEWER_STRINGS.ariaRegion;

  return (
    <Box
      className={`${classes.root}${className ? ` ${className}` : ''}`}
      style={rootStyle}
      role="region"
      aria-label={regionLabel}
    >
      {loadError ? (
        <Box className={classes.loadingContainer}>
          <Typography
            id="pdf-viewer-error"
            variant="body1"
            className={classes.error}
          >
            {PDF_VIEWER_STRINGS.error}
          </Typography>
        </Box>
      ) : (
        <div ref={scrollRef} className={classes.pdfViewerChrome}>
          {showBlockingOverlay && (
            <Box className={classes.loadingOverlay}>
              <Typography
                id="pdf-viewer-loading"
                variant="body1"
                className={classes.loadingText}
              >
                {PDF_VIEWER_STRINGS.loading}
              </Typography>
              <CircularProgress className={classes.loadingSpinner} size={32} />
            </Box>
          )}
          <Box className={classes.pdfPageStack}>
            <Document
              file={fileUrl}
              renderMode={renderMode}
              className={classes.pdfDocument}
              loading={null}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
            >
              {canRenderPages
                ? Array.from({ length: numPages }, (_, index) => {
                    const pageNumber = index + 1;
                    return (
                      <Page
                        key={`pdf-page-${pageNumber}`}
                        pageNumber={pageNumber}
                        width={pageWidth}
                        className={classes.pdfPage}
                        loading={null}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    );
                  })
                : null}
            </Document>
          </Box>
        </div>
      )}
    </Box>
  );
};

PdfViewer.propTypes = {
  classes: PropTypes.object.isRequired,
  fileUrl: PropTypes.string,
  fileName: PropTypes.string,
  renderMode: PropTypes.oneOf(['canvas', 'svg']),
  maxWidth: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
};

PdfViewer.defaultProps = {
  fileUrl: undefined,
  fileName: undefined,
  renderMode: 'svg',
  maxWidth: undefined,
  className: undefined,
  style: undefined,
};

export default withStyles(styles)(PdfViewer);
